import { useEntityChecklistContext } from "../context/EntityChecklistContext";
import { isChecklistItemChecked, type ChecklistItem } from "../types";
import { GripVerticalIcon, ImageIcon, TrashIcon, MessageSquare, Upload, X, RefreshCw, PencilIcon } from "lucide-react";
import { Button } from "@/modern/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/modern/components/ui/popover";
import { Textarea } from "@/modern/components/ui/textarea";
import { Editable, EditableArea, EditablePreview, EditableInput, EditableTrigger } from "@/modern/components/ui/editable";
import { useEffect, useState, useRef } from "react";
import { cn } from "@/modern/utils";
import { SortableItemHandle } from "@/modern/components/ui/sortable";
import { Dialog, DialogContent } from "@/modern/components/ui/dialog-shadcn";
import { FileUpload, FileUploadDropzone } from "@/modern/components/ui/file-upload";

export interface ChecklistItemRowUIConfig {
  showComment?: boolean;
  showDragHandle?: boolean;
  showImageHandler?: boolean;
  showDeleteHandler?: boolean;
  showButtonsOnHover?: boolean;
  /** When false, hide personal check toggles (e.g. matrix structure editor). */
  showCheckToggle?: boolean;
}

interface ChecklistItemRowProps extends ChecklistItemRowUIConfig {
  item: ChecklistItem;
}

const SHOW_ON_HOVER_CLS = "opacity-0 group-hover:opacity-100 transition-opacity";

export const ChecklistItemRow = ({
  item,
  showComment = true,
  showDragHandle = true,
  showImageHandler = true,
  showDeleteHandler = true,
  showButtonsOnHover = false,
  showCheckToggle = true
}: ChecklistItemRowProps) => {
  const {toggleChecklistItem, updateChecklistItem, deleteChecklistItem, uploadChecklistItemImage, removeChecklistItemImage, canEditSelectedChecklist, isUserAssignedToChecklist, selectedChecklist} = useEntityChecklistContext();
  const [isCommentOpen, setIsCommentOpen] = useState(false);
  const [commentText, setCommentText] = useState(item.comment || "");
  const [descriptionDraft, setDescriptionDraft] = useState(item.description);
  const [isImagePopoverOpen, setIsImagePopoverOpen] = useState(false);
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canUserEdit = canEditSelectedChecklist;
  const isUserAssigned = selectedChecklist ? isUserAssignedToChecklist(selectedChecklist) : false;
  const canCommentOrImage = canUserEdit || isUserAssigned;

  const handleCheckboxChange = () => {
    toggleChecklistItem(item);
  };

  const handleSaveComment = () => {
    updateChecklistItem(item, { comment: commentText });
    setIsCommentOpen(false);
  };

  useEffect(() => setDescriptionDraft(item.description), [item.description]);

  const handleUpdateDescription = (newDescription: string) => {
    if (newDescription.trim() && newDescription !== item.description) {
      updateChecklistItem(item, { description: newDescription });
    } else {
      setDescriptionDraft(item.description);
    }
  };

  const handleFileSelect = async (files: File[]) => {
    const file = files[0];
    if (!file) return;

    setIsUploading(true);
    const success = await uploadChecklistItemImage(item, file);
    setIsUploading(false);

    if (success) {
      setIsImagePopoverOpen(false);
    }
  };

  const handleFileSelectLegacy = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    const success = await uploadChecklistItemImage(item, file);
    setIsUploading(false);

    if (success) {
      setIsImagePopoverOpen(false);
    }

    // Reset input value to allow re-uploading the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveImage = async () => {
    setIsUploading(true);
    const success = await removeChecklistItemImage(item);
    setIsUploading(false);

    if (success) {
      setIsImagePopoverOpen(false);
    }
  };

  const handleReplaceImage = () => {
    fileInputRef.current?.click();
  };

  const hasComment = Boolean(item.comment?.trim());
  const hasImage = Boolean(item.image || item.thumbnailUrl);
  const checked = isChecklistItemChecked(item);
  const showDoneState = showCheckToggle && checked;

  return (
    <div className={cn("flex items-start gap-2 p-2.5 group bg-card rounded-lg border border-border/40", showDoneState && "bg-primary/10")}>
      {showDragHandle && (
        <SortableItemHandle asChild disabled={!canUserEdit} value={item.id}>
          <Button variant="ghost" size="icon" className={cn("mt-0.5 h-8 w-8 p-0", showButtonsOnHover ? SHOW_ON_HOVER_CLS : "")} disabled={!canUserEdit}>
            <GripVerticalIcon className="size-4 text-muted-foreground" />
          </Button>
        </SortableItemHandle>
      )}
      {showCheckToggle && (
        <Button variant="ghost" size="icon" className={cn("mt-0.5 h-8 w-8 p-0")} onClick={handleCheckboxChange} disabled={!canUserEdit && !isUserAssigned}>
          {checked ? <i className="fa-solid fa-circle-check text-xl text-primary" /> : <i className="fa-regular fa-circle text-xl text-muted-foreground" />}
        </Button>
      )}
      <div className="flex-1 min-w-0 flex items-start gap-2 pl-1">
        <div className="flex-1 min-w-0 space-y-1">
          {canUserEdit ? (
            <Editable
              autosize
              value={descriptionDraft}
              onValueChange={setDescriptionDraft}
              onSubmit={handleUpdateDescription}
              placeholder="Enter description..."
              triggerMode="click"
              className="flex max-w-full min-w-0 flex-1 flex-row items-start gap-0.5"
            >
              <EditableArea className="min-w-0 max-w-full data-[editing]:block data-[editing]:w-full data-[editing]:flex-1">
                <EditablePreview
                  className={cn(
                    "min-h-9 whitespace-pre-wrap break-words py-2 pl-0 pr-0 text-sm font-medium border-0",
                    showDoneState && "line-through text-muted-foreground"
                  )}
                />
                <EditableInput
                  asChild
                  className={cn(
                    "min-h-9 w-full resize-none overflow-hidden rounded-md border border-input bg-background px-3 text-sm font-medium",
                    "shadow-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0",
                    showDoneState && "line-through text-muted-foreground"
                  )}
                >
                  <Textarea rows={1} className="!min-h-9 resize-none overflow-hidden whitespace-pre-wrap break-words py-2" />
                </EditableInput>
              </EditableArea>
              <EditableTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className="inline-flex h-7 w-7 shrink-0 opacity-0 pointer-events-none transition-opacity text-muted-foreground hover:text-foreground group-hover:opacity-100 group-hover:pointer-events-auto"
                >
                  <PencilIcon className="size-3.5" />
                </Button>
              </EditableTrigger>
            </Editable>
          ) : (
            <div className={`text-sm font-medium ${showDoneState ? "line-through text-muted-foreground" : ""}`}>{item.description || "N/A"}</div>
          )}
          {showCheckToggle && checked && item.checkedBy ? (
            <p className="text-xs text-muted-foreground">Done by {item.checkedBy}</p>
          ) : null}
          {(hasComment || hasImage) && (
            <div className="flex items-center gap-2">
              {hasComment && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <MessageSquare className="size-3" />
                  <span>{item.comment}</span>
                </div>
              )}

              {hasImage && (
                <div className="w-8 h-8 rounded overflow-hidden bg-muted flex-shrink-0 cursor-pointer hover:ring-2 hover:ring-primary transition-all" onClick={() => setIsImageViewerOpen(true)}>
                  <img src={item.thumbnailUrl || item.image} alt="Preview" className="w-full h-full object-cover" />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-start gap-0.5">
        {showComment && (
          <Popover
            open={isCommentOpen}
            onOpenChange={(isOpen) => {
              setIsCommentOpen(isOpen);
              setCommentText(item.comment || "");
            }}
          >
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" className={cn("mt-0.5 h-8 w-8 p-0", showButtonsOnHover ? SHOW_ON_HOVER_CLS : "")} disabled={!canCommentOrImage}>
                <MessageSquare className={cn("size-4", hasComment ? "text-primary" : "text-muted-foreground")} />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="end">
              <Textarea placeholder="Add a comment..." value={commentText} onChange={(e) => setCommentText(e.target.value)} className="min-h-24 mb-3 resize-none" />
              <Button className="w-full" size="sm" variant="outline" onClick={handleSaveComment}>
                Save Comment
              </Button>
            </PopoverContent>
          </Popover>
        )}
        {showImageHandler && (
          <Popover open={isImagePopoverOpen} onOpenChange={setIsImagePopoverOpen}>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" className={cn("mt-0.5 h-8 w-8 p-0", showButtonsOnHover ? SHOW_ON_HOVER_CLS : "")} disabled={!canCommentOrImage}>
                <ImageIcon className={cn("size-4", hasImage ? "text-primary" : "text-muted-foreground")} />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="end">
              <input type="file" ref={fileInputRef} onChange={handleFileSelectLegacy} accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/heif" className="hidden" />

              {hasImage ? (
                <div className="space-y-3">
                  <div className="text-sm font-medium mb-2">Image</div>
                  <div className="relative rounded-md overflow-hidden bg-muted aspect-video">
                    <img src={(item.image || item.thumbnailUrl) + "?v=" + Date.now()} alt="Checklist item" className="w-full h-full object-contain" />
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1" size="sm" onClick={handleReplaceImage} disabled={isUploading}>
                      <RefreshCw className={cn("size-4", isUploading && "animate-spin")} />
                      Replace
                    </Button>
                    <Button variant="outline" className="flex-1" size="sm" onClick={handleRemoveImage} disabled={isUploading}>
                      <X className="size-4" />
                      Remove
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="text-sm font-medium mb-2">Upload Image</div>
                  <FileUpload
                    accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/heif"
                    maxFiles={1}
                    onAccept={handleFileSelect}
                    disabled={isUploading}
                  >
                    <FileUploadDropzone className="min-h-32">
                      <div className="text-center">
                        <Upload className="size-8 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground mb-1">
                          {isUploading ? "Uploading..." : "Click or drag to upload image"}
                        </p>
                        <p className="text-xs text-muted-foreground">PNG, JPG, SVG, HEIF</p>
                      </div>
                    </FileUploadDropzone>
                  </FileUpload>
                </div>
              )}
            </PopoverContent>
          </Popover>
        )}
        {showDeleteHandler && (
          <Button variant="ghost" size="icon" className={cn("mt-0.5 h-8 w-8 p-0", showButtonsOnHover ? SHOW_ON_HOVER_CLS : "")} disabled={!canUserEdit} onClick={() => deleteChecklistItem(item)}>
            <TrashIcon className="size-4 text-muted-foreground" />
          </Button>
        )}
      </div>

      {/* Image Viewer Dialog */}
      <Dialog open={isImageViewerOpen} onOpenChange={setIsImageViewerOpen}>
        <DialogContent className="z-[--swivu-bryntum-z-index] sm:max-w-3xl">
          <div className="relative overflow-hidden p-2">
            <img src={(item.image || item.thumbnailUrl) + "?v=" + Date.now()} alt="Checklist item" className="w-full h-auto rounded-lg max-h-[80vh] object-contain" />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
